/**
 * ① 관세율표 검색결과 — 세율·요건 비교 테이블 (검색결과 종합 뷰).
 * 호 상세 HeadingGrid 기반 9컬럼(호|소호|세번|한글|영문|기본세율|탄력·양허|요건|근거),
 * 잎(10자리)+호·소호 표제를 코드순 병합 (CLHS 관세율표 검색결과 패리티).
 * 최저 기본세율 잎 = ★ (전 잎 동률이면 표시 없음 — buildTariffCompareRows).
 * 행 클릭 → onRowClick(hs4, hsk|null) — 표제 행은 hsk 없이 호 상세로.
 */
import React, { useMemo, useState } from "react";
import { buildTariffCompareRows } from "./multiSearchData";
import { highlightTokens } from "./highlightTokens";
import SourceEvidenceLink, { tariffEvidenceUrl } from "./SourceEvidenceLink";

const COLLAPSE_LIMIT = 15;

export default function TariffCompareTable({
  items,
  headingMatches,
  matchTokens,
  onRowClick,
}) {
  const rows = useMemo(
    () => buildTariffCompareRows(items, headingMatches),
    [items, headingMatches],
  );
  const [open, setOpen] = useState(false);
  const tokens = useMemo(
    () =>
      (Array.isArray(matchTokens) ? matchTokens : [])
        .map((t) => (typeof t === "string" ? t.toLowerCase().trim() : ""))
        .filter((t) => t.length >= 2),
    [matchTokens],
  );
  if (rows.length === 0) return null;
  const visible = open ? rows : rows.slice(0, COLLAPSE_LIMIT);
  let lastHs4 = null; // 같은 호가 연속되면 호 셀 비움 (HeadingGrid 관례)
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          fontSize: "var(--audit-type-table-size)",
          lineHeight: "var(--audit-type-table-line)",
          borderCollapse: "collapse",
          background: "var(--audit-bg-elevated)",
          border: "1px solid var(--audit-border)",
        }}
      >
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: 30 }} />
          <col style={{ width: 46 }} />
          <col />
          <col />
          <col style={{ width: 56 }} />
          <col style={{ width: 84 }} />
          <col style={{ width: 46 }} />
          <col style={{ width: 44 }} />
        </colgroup>
        <thead>
          <tr style={{ background: "var(--audit-bg-subtle)" }}>
            <th style={thCenter}>호</th>
            <th style={thCenter}>소호</th>
            <th style={thCenter}>세번</th>
            <th style={thCenter}>한글</th>
            <th style={thCenter}>영문</th>
            <th style={thCenter}>기본세율</th>
            <th style={{ ...thCenter, textAlign: "left" }}>탄력·양허</th>
            <th style={{ ...thCenter, textAlign: "left" }}>요건</th>
            <th style={thCenter}>근거</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => {
            const isLeaf = row.type === "leaf";
            const showHs4 = row.hs4 !== lastHs4;
            lastHs4 = row.hs4;
            return (
              <tr
                key={row.key}
                onClick={() =>
                  onRowClick && onRowClick(row.hs4, isLeaf ? row.hsk : null)
                }
                style={{
                  cursor: onRowClick ? "pointer" : "default",
                  background: isLeaf ? undefined : "var(--audit-bg-subtle)",
                }}
                onMouseEnter={(e) => {
                  if (onRowClick)
                    e.currentTarget.style.background = "var(--audit-bg-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isLeaf
                    ? ""
                    : "var(--audit-bg-subtle)";
                }}
                title={
                  isLeaf
                    ? `${row.hsk} 세번 상세 보기`
                    : `제${row.hs4}호 상세 보기`
                }
              >
                <td style={hs4Cell}>{showHs4 ? row.hs4 : ""}</td>
                <td style={codeCell}>
                  {isLeaf ? row.sub2 : row.type === "subheading" ? row.code.slice(4) : ""}
                </td>
                <td style={codeCell}>{isLeaf ? row.tail4 : ""}</td>
                <td style={koCell}>
                  {highlightTokens(isLeaf ? row.ko : row.label, tokens)}
                </td>
                <td style={enCell}>
                  {highlightTokens(isLeaf ? row.en : row.labelEn, tokens)}
                </td>
                <td style={basicCell}>
                  {row.basicRate}
                  {row.isMinRate && (
                    <span
                      style={{ color: "var(--audit-accent)", marginLeft: 3 }}
                      title="검색결과 중 최저 기본세율"
                    >
                      ★
                    </span>
                  )}
                </td>
                <td style={tanCell}>{isLeaf ? row.tan : ""}</td>
                <td style={reqCell}>
                  {isLeaf && (row.hasImportReq || row.hasExportReq) ? (
                    <span style={{ display: "inline-flex", gap: 2 }}>
                      {row.hasImportReq && (
                        <span style={imBadge} title="수입요건 있음">
                          Im
                        </span>
                      )}
                      {row.hasExportReq && (
                        <span style={exBadge} title="수출요건 있음">
                          Ex
                        </span>
                      )}
                    </span>
                  ) : null}
                </td>
                <td style={{ ...reqCell, textAlign: "center" }}>
                  <SourceEvidenceLink
                    compact
                    iconOnly
                    url={tariffEvidenceUrl(isLeaf ? row.hsk : row.hs4)}
                    label="관세청 원문"
                    title={`${isLeaf ? row.hsk : `제${row.hs4}호`} 관세율표 원문 조회`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length > COLLAPSE_LIMIT && (
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            marginTop: 4,
            border: "none",
            background: "transparent",
            color: "var(--audit-accent)",
            fontSize: "var(--audit-type-label-size)",
            cursor: "pointer",
            fontFamily: "inherit",
            padding: "2px 8px",
          }}
        >
          {open ? "접기" : `전체 ${rows.length}건 보기`}
        </button>
      )}
    </div>
  );
}

const thCenter = {
  textAlign: "center",
  padding: "5px 8px",
  borderBottom: "1px solid var(--audit-border-strong)",
  fontSize: "var(--audit-type-label-size)",
  fontWeight: 600,
  color: "var(--audit-text-muted)",
};
const baseCell = {
  padding: "5px 6px",
  lineHeight: "19px",
  verticalAlign: "top",
  borderTop: "1px solid var(--audit-border)",
  borderRight: "1px solid var(--audit-border)",
};
const hs4Cell = {
  ...baseCell,
  textAlign: "center",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 700,
  fontSize: "var(--audit-type-table-size)",
};
const codeCell = {
  ...baseCell,
  textAlign: "center",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 600,
  fontSize: "var(--audit-type-table-size)",
};
const koCell = { ...baseCell, wordBreak: "keep-all", overflowWrap: "anywhere" };
const enCell = {
  ...baseCell,
  color: "var(--audit-text-muted)",
  fontSize: 12,
  overflowWrap: "anywhere",
};
const basicCell = {
  ...baseCell,
  textAlign: "center",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: "var(--audit-type-table-size)",
};
const tanCell = {
  ...baseCell,
  fontFamily: "JetBrains Mono, monospace",
  fontSize: "var(--audit-type-table-size)",
};
const reqCell = { ...baseCell };
const imBadge = {
  display: "inline-block",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "14px",
  padding: "0 3px",
  borderRadius: 2,
  background: "#1a7d3a",
  color: "#fff",
};
const exBadge = { ...imBadge, background: "#1a567d" };
